// apps/provider/src/services/api/fax.js
// E-Fax domain module — send, receive, list, and manage fax records.
//
// Methods: sendFax, getFaxes, getFax, updateFax, completeFax, resendFax,
//          getFaxReferralMatches, getUnreadFaxCount
//
// Response types: @prism-ehr/shared → Fax, FaxListResponse, FaxSendResult, FaxUnreadCount

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

/**
 * @returns {Object} Fax domain methods (bound to `this` of the composed apiService)
 */
export function createFaxMethods() {
  return {

    // ============ Send ============

    /**
     * Send an outbound fax. File is uploaded as base64.
     *
     * Xano: POST /fax/send-sinch
     * Xano staging file: fax_send_sinch_POST.xs
     *
     * @param {Object} faxData - Fax send payload
     * @param {string} faxData.to_number - Destination fax number
     * @param {string} faxData.file_data - Base64-encoded file
     * @param {string} faxData.file_name - Original file name
     * @param {string} faxData.file_type - MIME type
     * @param {number} faxData.file_size - File size in bytes
     * @param {string} [faxData.recipient_name] - Recipient name for cover sheet
     * @param {string} [faxData.subject] - Fax subject
     * @param {boolean} [faxData.cover_sheet_included] - Whether to prepend cover sheet
     * @param {string} [faxData.cover_sheet_message] - Message body on cover sheet
     * @param {number} [faxData.patient_id] - Optional patient association
     * @returns {Promise<import('@prism-ehr/shared').FaxSendResult>}
     */
    async sendFax(faxData, file) {
      // Convert file to base64 if a File object was provided
      let payload = { ...faxData };

      if (file) {
        const base64Data = await blobToBase64(file);
        payload.file_data = base64Data;
        payload.file_name = file.name;
        payload.file_type = file.type;
        payload.file_size = file.size;
      }

      return this.request('/fax/send-sinch', {
        method: 'POST',
        body: JSON.stringify(payload)
      }, 'sendFax');
    },

    // ============ List ============

    /**
     * List faxes with filters and pagination.
     *
     * Xano: GET /fax
     * Xano staging file: 1157_fax_GET.xs
     *
     * @param {Object} [params] - Query parameters
     * @param {number} [params.page] - Page number
     * @param {number} [params.per_page] - Items per page
     * @param {string} [params.direction] - 'inbound' or 'outbound'
     * @param {string} [params.status] - Filter by status
     * @param {string} [params.search] - Search term (number, name, subject)
     * @param {boolean} [params.unread_only] - Only show unread inbound faxes
     * @param {boolean} [params.completed] - true = only faxes marked complete; absent = only open faxes
     * @param {number} [params.patient_id] - Filter by linked patient
     * @returns {Promise<import('@prism-ehr/shared').FaxListResponse>}
     */
    getFaxes(params = {}) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.direction) queryParams.append('direction', params.direction);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.unread_only !== undefined) queryParams.append('unread_only', params.unread_only);
      if (params.patient_id) queryParams.append('patient_id', params.patient_id);
      if (params.referral_match_only) queryParams.append('referral_match_only', params.referral_match_only);
      if (params.completed) queryParams.append('completed', params.completed);

      const qs = queryParams.toString();
      return this.request(`/fax${qs ? `?${qs}` : ''}`, {
        method: 'GET'
      });
    },

    // ============ Detail ============

    /**
     * Get a single fax record by ID.
     *
     * Xano: GET /fax/{fax_id}
     * Xano staging file: 1158_fax_fax_id_GET.xs
     *
     * @param {number} faxId - Fax record ID
     * @returns {Promise<import('@prism-ehr/shared').Fax>}
     */
    getFax(faxId) {
      return this.request(`/faxes/${faxId}`, {
        method: 'GET'
      });
    },

    // ============ Update ============

    /**
     * Update a fax record (mark read, link patient, add notes).
     *
     * Xano: PATCH /fax/{fax_id}
     * Xano staging file: 1162_fax_fax_id_PATCH.xs
     *
     * @param {number} faxId - Fax record ID
     * @param {Object} updates - Fields to update
     * @param {number} [updates.patient_id] - Link to patient
     * @param {string} [updates.notes] - Notes
     * @param {boolean} [updates.mark_read] - Mark as read by current user
     * @returns {Promise<import('@prism-ehr/shared').Fax>}
     */
    updateFax(faxId, updates) {
      return this.request(`/faxes/${faxId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    // ============ Complete / Reopen ============

    /**
     * Mark a fax complete (handled) or reopen it. Stamps completed_at and
     * completed_by (acting user); separate from the transmission status.
     *
     * Xano: POST /fax-complete
     *
     * @param {string} faxId - Fax record ID
     * @param {boolean} [completed=true] - false reopens the fax
     * @returns {Promise<import('@prism-ehr/shared').Fax>}
     */
    completeFax(faxId, completed = true) {
      return this.request('/fax-complete', {
        method: 'POST',
        body: JSON.stringify({ fax_id: faxId, completed })
      });
    },

    // ============ Resend ============

    /**
     * Resend a failed outbound fax from the document stored on the row.
     * Nothing is re-entered: the backend reads the private attachment back
     * and re-sends through Sinch, reusing the same fax row (new Sinch id,
     * status back to "sending", metadata.retry_count incremented).
     *
     * Xano: POST /fax-resend
     * Xano file: fax-resend_POST.xs
     *
     * @param {string} faxId - Fax record ID (outbound, status "failed")
     * @returns {Promise<import('@prism-ehr/shared').FaxSendSinchResult>}
     */
    resendFax(faxId) {
      return this.request('/fax-resend', {
        method: 'POST',
        body: JSON.stringify({ fax_id: faxId })
      }, 'resendFax');
    },

    // ============ Referral Matching ============

    /**
     * Check fax numbers against referring providers and organizations.
     *
     * Xano: POST /fax-referral-matches
     * Xano staging file: 1343_fax_referral_matches_POST.xs
     *
     * @param {string[]} faxNumbers - Array of fax numbers to check
     * @returns {Promise<import('@prism-ehr/shared').FaxReferralMatchesResponse>}
     */
    async getFaxReferralMatches(faxNumbers) {
      return this.request('/fax-referral-matches', {
        method: 'POST',
        body: JSON.stringify({ fax_numbers: faxNumbers })
      });
    },

    // ============ Unread Count ============

    /**
     * Get count of unread inbound faxes for badge display.
     *
     * Xano: GET /fax/unread-count
     * Xano staging file: 1161_fax_unread_count_GET.xs
     *
     * @returns {Promise<number>}
     */
    async getUnreadFaxCount() {
      try {
        const result = await this.request('/fax/unread-count', {
          method: 'GET'
        });
        return result?.count || 0;
      } catch (error) {
        console.error('Error fetching unread fax count:', error);
        return 0;
      }
    },
  };
}
